import { Card, CardBody, CardFooter } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useUndoableDelete } from 'src/utils/useUndoableDelete';
import { AddNewButton, BulkDeleteButton, UsageRow } from '../..';

import {
  Commercial,
  CommercialType,
  Role,
  TCommercialId,
  TUpdateCommercialRequest,
  TUsageId,
  Usage,
  UsageType,
  UsageUnitType,
} from '@talon/web/lib/api';
import {
  useCommercialStore,
  useEstimateStore,
  useProjectStore,
  useRoleStore,
  useUsageStore,
} from '@talon/web/src/context';
import { getCommercialRoles } from 'src/utils/activityRoles';
import { getMatchingUsageTypeInfos } from 'src/utils/usages';
import { CommercialCardHeader } from '../commercials/CommercialCardHeader';
import { LabeledField } from '../LabeledField';
import { ContractSelect } from '../estimate/ContractSelect';
import { EmptyState } from '@talon/web/src/components/ui/EmptyState';

export type CommercialUsageRowProps = {
  commercialId: TCommercialId;
  onChecked: () => void;
  checked: boolean;
};

export const CommercialUsageRow = observer(({ commercialId, onChecked, checked }: CommercialUsageRowProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();
  const usageStore = useUsageStore();
  const commercialStore = useCommercialStore();
  const projectStore = useProjectStore();
  const roleStore = useRoleStore();

  /** State **/
  const [commercial, setCommercial] = useState<Commercial>();
  const [selectedUsageIds, setSelectedUsageIds] = useState<TUsageId[]>([]);
  const [hiddenUsageIds, setHiddenUsageIds] = useState<TUsageId[]>([]);
  const deleteWithUndo = useUndoableDelete();
  const [roles, setRoles] = useState<Role[]>();
  const [wages, setWages] = useState(0);
  const [total, setTotal] = useState(0);

  /** Computed **/
  const tempEstimate = estimateStore.tempEstimate;
  const usageEstimate = estimateStore.getTempUsageEstimate(commercial?.id);

  /** Methods **/

  const recalculateUsageEstimate = async () => {
    if (!commercial) return;

    const usageEstimate = tempEstimate?.usage_estimates.find(
      (usageEstimate) => usageEstimate.commercial?.id === commercial.id
    );

    if (usageEstimate) await estimateStore.updateUsageEstimate(usageEstimate.id, undefined, true);
  };

  const chooseUsage = (id: TUsageId) => {
    if (selectedUsageIds.includes(id)) {
      setSelectedUsageIds(selectedUsageIds.filter((s) => s !== id));
    } else {
      setSelectedUsageIds([...selectedUsageIds, id]);
    }
  };

  const updateCommercial = async (update: TUpdateCommercialRequest) => {
    if (!commercial) return;
    const updatedCommercial = await commercialStore.updateCommercial(commercial.id, update);

    if (updatedCommercial) {
      setCommercial(updatedCommercial);

      // Refresh the temp estimate
      await recalculateUsageEstimate();
    }
  };

  const addUsage = async (usageType?: UsageType) => {
    if (!commercial) return;

    const usage = await usageStore.createUsage({
      commercialId,
      title: `${usageType} Usage`,
      type: usageType ?? null,
      cycles: 1,
      units: 1,
      unit_type: UsageUnitType.Uses,
      role_ids: [],
      platform: null,
      category: null,
      uses_type: null,
      cycle_number: null,
      cycle_unit: null,
      language: null,
      market_spec: null,
      global_market: null,
      cable_market: null,
      audio_made_move: null,
    });

    if (usage) {
      const usages = [...commercial.usages, usage];
      const updatedCommercial = { ...commercial, usages };

      const usageEstimates = (await commercialStore.getCommercial(commercial.id))?.usage_estimates;
      if (usageEstimates) updatedCommercial.usage_estimates = usageEstimates;

      setCommercial(updatedCommercial);

      // Refresh the temp estimate
      await recalculateUsageEstimate();
    }
  };

  const updateUsage = async (usage: Usage) => {
    if (!commercial) return;

    // Match this Usage to UsageTypes
    const matchingTypeInfos = getMatchingUsageTypeInfos(usage, commercial);

    const {
      id,
      roles,
      units,
      unit_type,
      cycles,
      cycle_number,
      cycle_unit,
      platform,
      category,
      uses_type,
      market_spec,
      language,
      global_market,
      cable_market,
      audio_made_move,
    } = usage;

    let title = 'Untyped Usage';
    let type = null;

    if (usage.type) {
      title = usage.type;
      type = usage.type;
    } else if (matchingTypeInfos.length === 1) {
      title = matchingTypeInfos[0].id;
      type = matchingTypeInfos[0].id;
    }

    const updatedUsage = await usageStore.updateUsage(id, {
      role_ids: roles?.map((r) => r.id),
      title: title,
      type: type,
      units: units,
      unit_type: unit_type,
      cycles: cycles,
      cycle_number: cycle_number,
      cycle_unit: cycle_unit,
      platform: platform,
      category: category,
      uses_type: uses_type,
      market_spec: market_spec,
      language: language,
      global_market: global_market,
      cable_market: cable_market,
      audio_made_move: audio_made_move,
    });
    if (!updatedUsage) return;

    const usages = commercial.usages?.map((u) => (u.id === updatedUsage.id ? updatedUsage : u));
    const updatedCommercial = { ...commercial, usages };
    setCommercial(updatedCommercial);

    // Refresh the temp estimate
    await recalculateUsageEstimate();
  };

  const deleteUsages = async () => {
    if (!commercial || selectedUsageIds.length === 0) return;

    const deleted: string[] = [];
    for (const id of selectedUsageIds) {
      if (await usageStore.deleteUsage(id)) deleted.push(id);
    }
    if (!deleted.length) return;

    const usages = commercial.usages.filter((usage) => !deleted.includes(usage.id));
    setCommercial({ ...commercial, usages });
    setSelectedUsageIds([]);
    // Refresh the temp estimate
    try {
      await recalculateUsageEstimate();
    } catch (error: unknown) {
      console.error(error);
      toast.warning('Usages deleted, but estimate totals could not refresh. Reload to update them.');
    }
  };

  /** Effects **/

  useEffect(() => {
    if (usageEstimate) {
      let wages = 0;
      let total = 0;

      for (const item of usageEstimate.items) {
        wages += item.subtotal;
        total += item.subtotal_plus_fringe;
      }

      setWages(wages);
      setTotal(total);
    }
  }, [usageEstimate]);

  useEffect(() => {
    const getCommercial = async (id: TCommercialId) => {
      if (!projectStore.currentProject) return;
      const c = await commercialStore.getCommercial(id);
      if (c) setCommercial(c);
    };

    getCommercial(commercialId);
  }, [commercialId, projectStore.currentProject]);

  useEffect(() => {
    if (commercial?.type) {
      const getRoles = async (commercialType: CommercialType) => {
        if (!projectStore.currentProject) return;
        const r = await roleStore.getRolesByProjectId(projectStore.currentProject.id);
        setRoles(getCommercialRoles(commercialType, r ?? []));
      };

      getRoles(commercial.type);
    }
  }, [commercial?.type, projectStore.currentProject]);

  /** Render **/

  return (
    <Card
      round="10px"
      border={{ color: 'border-card' }}
      elevation="none"
      background="white"
    >
      {/* Header */}
      <CommercialCardHeader
        commercial={commercial}
        checked={checked}
        onChecked={onChecked}
        onTitle={(title) => updateCommercial({ title })}
        onType={(type) => updateCommercial({ type })}
        onDuration={(durationInSeconds) => updateCommercial({ durationInSeconds })}
        summary={`${commercial?.usages?.length ?? 0} ${commercial?.usages?.length === 1 ? 'usage' : 'usages'}`}
        wages={wages}
        total={total}
      >
        {/* Saved on the commercial, so its usage estimates use this contract unless they choose their own */}
        <LabeledField label="Contract">
          <ContractSelect
            hideLabel
            editable={!!commercial}
            automaticBy="shoot date"
            appliedId={usageEstimate?.rate_schedule_id}
            overrideId={commercial?.rate_schedule_override_id}
            onChange={(rateScheduleId) => updateCommercial({ rateScheduleOverrideId: rateScheduleId })}
          />
        </LabeledField>
      </CommercialCardHeader>

      {/* Body */}
      <CardBody background="white">
        {(commercial?.usages ?? []).some((usage) => !hiddenUsageIds.includes(usage.id)) ? (
          commercial?.usages?.filter((usage) => !hiddenUsageIds.includes(usage.id)).map((usage, i) => (
            <UsageRow
              key={usage.id}
              usage={usage}
              commercial={commercial}
              roles={roles ?? []}
              onChecked={() => chooseUsage(usage.id)}
              checked={selectedUsageIds.includes(usage.id)}
              updateUsage={(update) => updateUsage({ ...usage, ...update })}
              onDelete={() =>
                deleteWithUndo({
                  title: usage.title || 'Usage',
                  hide: () => {
                    setHiddenUsageIds((ids) => [...ids, usage.id]);
                    setSelectedUsageIds((ids) => ids.filter((id) => id !== usage.id));
                  },
                  restore: () => setHiddenUsageIds((ids) => ids.filter((id) => id !== usage.id)),
                  commit: async () => {
                    const deleted = await usageStore.deleteUsage(usage.id);
                    if (!deleted) return false;
                    setCommercial((current) => current && {
                      ...current,
                      usages: current.usages.filter((item) => item.id !== usage.id),
                    });
                    setHiddenUsageIds((ids) => ids.filter((id) => id !== usage.id));
                    try {
                      await recalculateUsageEstimate();
                    } catch (error: unknown) {
                      console.error(error);
                      toast.warning('Usage deleted, but estimate totals could not refresh. Reload to update them.');
                    }
                    return true;
                  },
                })
              }
              rowIndex={i}
            />
          ))
        ) : (
          <EmptyState
            title="No usage yet"
            body="Add where and how long this spot will air to estimate its usage fees."
            actionLabel={commercial ? 'Add usage' : undefined}
            onAction={() => addUsage()}
          />
        )}
      </CardBody>

      {/* Footer */}
      <CardFooter
        justify="between"
        gap="small"
        background="white"
        border={{ side: 'top', color: 'border-divider' }}
        pad={{ vertical: 'small', horizontal: '16px' }}
      >
        <AddNewButton
          plain
          label="Add usage"
          tipText="Add Usage Data"
          disabled={!commercial}
          onClick={() => commercial && addUsage()}
        />
        <BulkDeleteButton
          count={selectedUsageIds.length}
          singular="usage"
          plural="usages"
          names="the selected usages"
          onDelete={deleteUsages}
        />
      </CardFooter>
    </Card>
  );
});
